import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import withRouter from '../../../components/Common/withRouter'
import GlobalLoader from '../../../components/Common/GlobalLoader'
import { createSelector } from "reselect"
import { useSelector, useDispatch } from "react-redux"
import Select from "react-select"
import makeAnimated from "react-select/animated"
import * as XLSX from 'xlsx'
import {
    getAllCategories as onGetAllCategories,
    getWarehouseStock as onGetWarehouseStock,
    exportWarehouseStock as onExportWarehouseStock,
  } from "../../../slice/thunks"
import { Col, Input, Label, Row } from 'reactstrap'
import TableContainerWarehouse from '../../../components/Common/TableContainerWarehouse'
import { toast } from 'react-toastify'

function StockReports(props) {
    const dispatch = useDispatch()
    const animatedComponents = makeAnimated()
    const outputLayoutState = (state) => state.Settings
    const OutputsProperties = createSelector(
        outputLayoutState,
        (setting) =>({
            stockReports: setting.stockReports,
            all_categories: setting.all_categories,
            error: setting.error
        })
    )
    const { all_categories, stockReports, error } = useSelector(OutputsProperties)
    const [categoriesOptions, setCategoriesOptions] = useState([{label: props.t("all"), value: null}])
    const [selectedCategories, setselectedCat] = useState(null)
    const [searchInput, setSearchInput] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false)

    const loadStock = useCallback((pageNumber, pageSize, term) => {

        if (pageNumber && pageSize) {
            if (term) {
                if (selectedCategories !== null && selectedCategories.value !== null) {
                    dispatch(onGetWarehouseStock({page: pageNumber, size: pageSize, term: searchInput, category: selectedCategories.value}))
                } else {
                    dispatch(onGetWarehouseStock({page: pageNumber, size: pageSize, term: searchInput}))
                }
            }
            else {

                if (selectedCategories !== null && selectedCategories.value !== null) {
                    dispatch(onGetWarehouseStock({page: pageNumber, size: pageSize, category: selectedCategories.value}))
                }else {
                    dispatch(onGetWarehouseStock({page: pageNumber, size: pageSize}))
                }
            }
        } else {

            if (term) {
                dispatch(onGetWarehouseStock({page: 1, size: 30, term: searchInput}))
            }
            else {
                dispatch(onGetWarehouseStock({page: 1, size: 30}))
            }
        }
    })

    const loadCategories = useCallback(() => {
        dispatch(onGetAllCategories())
    })

    const handleExportStock = async () => {
        try {
            setIsExporting(true)
            
            const dataToExport = await dispatch(onExportWarehouseStock()).unwrap()
            
            console.log("Data to export:", dataToExport)
            
            // Vérifier si nous avons des données
            if (!dataToExport || !Array.isArray(dataToExport) || dataToExport.length === 0) {
                toast.warning(props.t("no_data_to_export") || "Aucune donnée à exporter")
                setIsExporting(false)
                return
            }

            // Préparer les données pour Excel 
            const exportData = dataToExport.map((item, index) => ({
                [props.t("s_no") || "N°"]: index + 1,
                [props.t("product_name") || "Nom du produit"]: item.name || "",
                [props.t("product_ref") || "Référence"]: item.productRef || "",
                [props.t("qty_in_warehouse") || "Qté entrepôt"]: item.qtyInWarehouse || 0,
                [props.t("qty_in_pharmacy") || "Qté pharmacie"]: item.qtyInPharmacy || 0,
            }))

            // Créer un nouveau classeur
            const ws = XLSX.utils.json_to_sheet(exportData)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, props.t("stock_reports") || "Stock Reports")

            // Définir la largeur des colonnes
            ws['!cols'] = [
                { wch: 5 },   // N°
                { wch: 30 },  // Nom du produit
                { wch: 20 },  // Référence
                { wch: 15 },  // Qté entrepôt
                { wch: 15 },  // Qté pharmacie
            ]

            // Générer le fichier Excel et le télécharger
            const date = new Date().toISOString().split('T')[0]
            XLSX.writeFile(wb, `stock_report_${date}.xlsx`)
            
            toast.success(props.t("export_success") || "Export réussi!")
        } catch (error) {
            console.error("Export error:", error)
            toast.error(props.t("export_error") || "Erreur lors de l'export")
        } finally {
            setIsExporting(false)
        }
    }

    useEffect(() => {
        loadStock(1, 30);
        loadCategories();
    },[dispatch])


    useEffect(() => {
        if (stockReports && stockReports.page > 0) {
            setIsLoading(false)
        }
    },[stockReports])

    useEffect(() =>{
        if (all_categories && all_categories.length > 0) {
            const categories_list = all_categories.map( category => {
                return { label: category.name, value: category.id }
            })
            setCategoriesOptions([...categoriesOptions, ...categories_list])
        }
    },[all_categories])

    useEffect(() => {
        if ((searchInput.length !== null) && (searchInput.length > 2) ) {
            loadStock(1, 30, searchInput)
        } else {
            loadStock(1, 30)
        }
    },[selectedCategories, searchInput])

    const handleSelectCategory = (selectedCat) => {
        setselectedCat(selectedCat)
    }

    const handleSearch = () => {
        loadStock(1, 30, searchInput)
    }

    const columns = useMemo(
        () => [
            {
                header: props.t("s_no"),
                style: "cat",
                enableColumnFilter: false,
                cell: (cellProps) => {
                    return <span>
                        {stockReports.page === 1 ? (cellProps.row.index + 1) : (((stockReports.page-1)* (stockReports.size?? 30) )+cellProps.row.index + 1) }
                    </span>;
                },
            },
           {
                header: props.t("product_name"),
                style: "cat",
                enableColumnFilter: false,
                cell: (cell) => {
                    return (
                        <span 
                            style={{ cursor: 'pointer'}}
                            onClick={() => props.router.navigate(`/stock-transaction/${cell.row.original.productId}`)}
                        >
                            {cell.row.original.name}
                        </span>
                    )
                }
            },
            {
                header: props.t("product_ref"),
                style: "type",
                enableColumnFilter: false,
                cell: (cell) => {
                return <span>{cell.row.original.productRef}</span>
                }
            },
            {
                header: props.t("qty_in_warehouse"),
                style: "sale",
                enableColumnFilter: false,
                cell: (cell) => {
                return <span>{cell.row.original.qtyInWarehouse}</span>
                }
            },
            {
                header: props.t("qty_in_pharmacy"),
                style: "sale",
                enableColumnFilter: false,
                cell: (cell) => {
                return <span>{cell.row.original.qtyInPharmacy}</span>
                }
            }
        ],
        [stockReports]
    );


  return (
    <React.Fragment>
        {isLoading ?
            <GlobalLoader />
            :
            <>
                <div className="patients-list-content">
                    <div className="patients-list px-2 pb-5 ">
                        <div className="container-fluid ">

                            <div className="heading-parent-sec ">
                                <div className="heading-child-one-sec pt-4">
                                    <h6>{props.t("stock_reports")}</h6>
                                </div>
                            </div>

                            <div className="card border-0 row p-2">
                                <div className="mt-3" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                                    <Row className='m-2 pb-3 align-items-end'>
                                        <Col xl={4} >
                                            <div className='mb-2'>
                                                <Label htmlFor="status-field" className="form-label">{props.t("categories")}</Label><br/>
                                                <Select
                                                    name='status'
                                                    id='status-field'
                                                    value={selectedCategories}
                                                    isMulti={false}
                                                    style={{ zIndex: 9999 }}
                                                    isClearable={true}
                                                    onChange={(e) => {
                                                        handleSelectCategory(e);
                                                        if (!e) {
                                                            loadStock(1, 30);
                                                        }
                                                    }}
                                                    options={categoriesOptions}
                                                    closeMenuOnSelect={true}
                                                    components={animatedComponents}
                                                />
                                            </div>
                                        </Col>
                                        <Col>
                                            <div className='mb-2'>
                                                <Label htmlFor="status-field" className="form-label">{props.t("search_product")}</Label><br/>
                                                <div className="d-flex align-items-center">
                                                    <div className="search-box me-2 col-8">
                                                        <Input
                                                            className="custom-border form-control"
                                                            value={searchInput}
                                                            onChange={(e) => setSearchInput(e.target.value)}
                                                        />
                                                   </div>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleSearch()} 
                                                        className="btn btn-primary me-2"
                                                    >
                                                        <i className="fa fa-search p-0"></i>
                                                    </button>
                                                  
                                                </div>
                                            </div>
                                        </Col>
                                        <Col className="d-flex justify-content-end">
                                                    <div className='mb-2'>
                                                      <button 
                                                        type="button" 
                                                        onClick={handleExportStock} 
                                                        className="btn btn-success"
                                                        disabled={isExporting}
                                                    >
                                                        {isExporting ? (
                                                            <>
                                                                <i className="fa fa-spinner fa-spin me-1"></i>
                                                                {props.t("exporting") || "Export..."}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fa fa-file-excel me-1"></i>
                                                                {props.t("export") || "Export"}
                                                            </>
                                                        )}
                                                    </button></div>
                                        </Col>
                                    </Row>
                                    <div className="send-patient-to-doctor-main-sec pb-3">
                                        <TableContainerWarehouse
                                            columns={columns}
                                            data={((stockReports && stockReports.items) || [])}
                                            isGlobalFilter={false}
                                            customPageSize={stockReports.size?? 30}
                                            divClass="card table-card table-warehouse table-responsive"
                                            tableClass="table"
                                            theadClass="thead-sec table-light"
                                            tdClass="td"
                                            tableSize={ stockReports && stockReports.size }
                                            tablePage={ stockReports && stockReports.page }
                                            tableTotal= { stockReports && stockReports.total_items }
                                            tableTotalPage= { stockReports && stockReports.total_page }
                                            onNextPage= {loadStock}
                                            onSearch= {handleSearch}
                                            SearchPlaceholder={props.t("search_product")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </>
        }
    </React.Fragment>
  )
}

export default withRouter(withTranslation()(StockReports))